import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input, Label } from '../../components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/state';
import { api } from '../../shared/api';
import type { PublicPhoto } from './PublicStayGalleryPage';
import { publicGalleryCacheTime, publicGalleryQueryKey } from './public-gallery-query';

type LookupKind = 'stay' | 'daycare';

export function PhotoCodeLookupPage() {
  return (
    <main className="min-h-screen bg-[#f8faf6] px-5 py-8 text-[#223126]">
      <div className="mx-auto max-w-5xl">
        <PhotoCodeLookup />
      </div>
    </main>
  );
}

export function PhotoCodeLookup() {
  const [draftCode, setDraftCode] = useState('');
  const [lookup, setLookup] = useState<{ code: string; kind: LookupKind } | null>(null);
  const galleryQuery = useQuery({
    queryKey: publicGalleryQueryKey(lookup?.kind ?? 'stay', lookup?.code ?? ''),
    queryFn: async () => {
      if (!lookup) return { code: '', kind: 'stay' as LookupKind, label: '', photos: [] as PublicPhoto[] };
      if (lookup.kind === 'daycare') {
        const gallery = await api.publicDaycareGallery(lookup.code);
        return { label: 'Galería de guardería', photos: gallery.photos };
      }
      const gallery = await api.publicGallery(lookup.code);
      return { label: `Galería #${gallery.shortId}`, photos: gallery.photos };
    },
    enabled: Boolean(lookup),
    staleTime: publicGalleryCacheTime,
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const code = draftCode.trim();
    if (!code) return;
    setLookup({ code, kind: code.toLowerCase().startsWith('daycare_') ? 'daycare' : 'stay' });
  };

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <div>
            <h2 className="text-lg font-semibold text-[#223126]">Ver fotos por código</h2>
            <p className="mt-1 text-sm text-[#687461]">Pega el código que te dio el equipo para consultar las fotos de la visita.</p>
          </div>
        </CardHeader>
        <CardContent>
          <form className="grid gap-3 sm:grid-cols-[1fr_auto]" onSubmit={submit}>
            <div className="grid gap-1.5">
              <Label htmlFor="photoCode">Código</Label>
              <Input id="photoCode" value={draftCode} placeholder="Ej. HB1234" onChange={(event) => setDraftCode(event.target.value)} />
            </div>
            <Button type="submit" className="self-end">
              <Search className="h-4 w-4" aria-hidden="true" />
              Ver fotos
            </Button>
          </form>
        </CardContent>
      </Card>

      {galleryQuery.isLoading ? <LoadingState label="Cargando fotos" /> : null}
      {galleryQuery.isError ? <ErrorState message={galleryQuery.error.message} /> : null}
      {galleryQuery.data && !galleryQuery.isLoading && !galleryQuery.isError ? (
        galleryQuery.data.photos.length > 0 ? (
          <PublicPhotoGrid code={lookup?.code ?? ''} kind={lookup?.kind ?? 'stay'} label={galleryQuery.data.label} photos={galleryQuery.data.photos} />
        ) : (
          <EmptyState title="Aún no hay fotos" body="Revisa el código o intenta más tarde." />
        )
      ) : null}
    </div>
  );
}

function PublicPhotoGrid({ code, kind, label, photos }: { readonly code: string; readonly kind: LookupKind; readonly label: string; readonly photos: PublicPhoto[] }) {
  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-xl font-semibold text-[#223126]">Fotos de Happy Bark</h3>
        <p className="mt-1 text-sm text-[#687461]">{label}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {photos.map((photo) => (
          <Link key={photo.id} to={`/photo-view/${kind}/${encodeURIComponent(code)}/${encodeURIComponent(photo.id)}`} className="overflow-hidden rounded-lg border border-[#d9dfd1] bg-white shadow-sm">
            <img src={photo.downloadUrl} alt={photo.fileName} className="aspect-square w-full object-cover" />
            <div className="p-3 text-sm font-medium">{photo.fileName}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}
